import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import SearchIcon from "@material-ui/icons/Search";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";

import useSmqPermissions from "../../../hooks/useSmqPermissions";
import SMQMainContainer from "../../../components/SMQMainContainer";
import ActionService from "../../../services/smq/ActionService";
import useTriggerTypesStyles from "./TriggerTypes.styles";

const EMPTY_FORM = { action_code: "", action_name: "" };

function ActionTypes() {
  const classes = useTriggerTypesStyles();
  const { isAdmin: isSmqAdmin } = useSmqPermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const notify = useCallback((message, severity = "success") => {
    setNotification({ open: true, message, severity });
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ActionService.getAll();
      setItems(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setItems([]);
      notify(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const filteredItems = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase("fr");
    if (!query) return items;
    return items.filter((item) =>
      `${item.action_code || ""} ${item.action_name || ""}`
        .toLocaleLowerCase("fr")
        .includes(query)
    );
  }, [filter, items]);

  useEffect(() => {
    setPage(0);
  }, [filter, rowsPerPage]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredItems.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredItems.length, page, rowsPerPage]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  const openCreateModal = () => {
    if (!isSmqAdmin) return;
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setFormOpen(true);
  };

  const openEditModal = (item) => {
    if (!isSmqAdmin) return;
    setEditingItem(item);
    setForm({
      action_code: item.action_code || "",
      action_name: item.action_name || "",
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setFormErrors({});
  };

  const handleFormChange = ({ target: { name, value } }) => {
    setForm((current) => ({
      ...current,
      [name]: name === "action_code" ? value.toUpperCase() : value,
    }));
    setFormErrors((current) => ({ ...current, [name]: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isSmqAdmin) return;
    const errors = {};
    if (!form.action_code.trim()) errors.action_code = "Code obligatoire";
    if (!form.action_name.trim()) errors.action_name = "Nom obligatoire";
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    const payload = {
      action_code: form.action_code.trim().toUpperCase(),
      action_name: form.action_name.trim(),
    };
    setSaving(true);
    try {
      if (editingItem) {
        await ActionService.update(editingItem.action_id, payload);
        notify("Type d’action modifié avec succès");
      } else {
        await ActionService.create(payload);
        notify("Type d’action ajouté avec succès");
      }
      setFormOpen(false);
      setEditingItem(null);
      await loadItems();
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isSmqAdmin || !itemToDelete) return;
    setDeleting(true);
    try {
      await ActionService.remove(itemToDelete.action_id);
      setItemToDelete(null);
      notify("Type d’action supprimé avec succès");
      await loadItems();
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  const closeNotification = () =>
    setNotification((current) => ({ ...current, open: false }));

  return (
    <SMQMainContainer title="Types d’action">
      <div className={classes.page}>
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div className={classes.headingGroup}>
              <div className={classes.headingIcon}>
                <SettingsOutlinedIcon fontSize="small" />
              </div>
              <div>
                <h2 className={classes.heading}>Types d’action</h2>
                
              </div>
            </div>
            {isSmqAdmin && <Button className={classes.createButton} onClick={openCreateModal}
              startIcon={<AddIcon />} variant="contained">
              Nouveau type
            </Button>}
          </div>

          <div className={classes.filterBar}>
            <TextField
              className={classes.searchField}
              inputProps={{ "aria-label": "Filtrer les types d’action" }}
              InputProps={{ startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ) }}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Rechercher par code ou par nom…"
              size="small"
              value={filter}
              variant="outlined"
            />
            <span className={classes.resultCount}>
              {filteredItems.length} résultat{filteredItems.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className={classes.tableWrap}>
            <Table aria-label="Liste des types d’action" className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.headCell}>Code</TableCell>
                  <TableCell className={classes.headCell}>Nom</TableCell>
                  <TableCell className={classes.headCell} width={130}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow><TableCell className={classes.stateCell} colSpan={3}>
                    <div className={classes.loading}>
                      <CircularProgress size={22} />Chargement des types d’action…
                    </div>
                  </TableCell></TableRow>
                )}
                {!loading && paginatedItems.length === 0 && (
                  <TableRow><TableCell className={classes.stateCell} colSpan={3}>
                    {filter ? "Aucun type ne correspond à votre recherche." :
                      "Aucun type d’action enregistré."}
                  </TableCell></TableRow>
                )}
                {!loading && paginatedItems.map((item) => (
                  <TableRow hover key={item.action_id}>
                    <TableCell className={classes.bodyCell}>
                      <span className={classes.codeBadge}>{item.action_code}</span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>{item.action_name}</TableCell>
                    <TableCell className={classes.bodyCell}>
                      {isSmqAdmin && <div className={classes.actions}>
                        <Tooltip title="Modifier"><IconButton
                          aria-label={`Modifier ${item.action_code}`}
                          className={classes.editButton}
                          onClick={() => openEditModal(item)} size="small">
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton></Tooltip>
                        <Tooltip title="Supprimer"><IconButton
                          aria-label={`Supprimer ${item.action_code}`}
                          className={classes.deleteButton}
                          onClick={() => setItemToDelete(item)} size="small">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton></Tooltip>
                      </div>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <TablePagination
            className={classes.pagination}
            component="div"
            count={filteredItems.length}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            labelRowsPerPage="Lignes par page"
            onChangePage={(_, nextPage) => setPage(nextPage)}
            onChangeRowsPerPage={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </div>
      </div>

      <Dialog aria-labelledby="action-form-title" classes={{ paper: classes.dialogPaper }}
        fullWidth onClose={closeFormModal} open={formOpen}>
        <form onSubmit={handleSubmit}>
          <DialogTitle className={classes.dialogTitle} id="action-form-title">
            {editingItem ? "Modifier le type d’action" : "Ajouter un type d’action"}
          </DialogTitle>
          <DialogContent>
            <p className={classes.dialogSubtitle}>Renseignez le code court et le libellé complet.</p>
            <div className={classes.formGrid}>
              <TextField autoFocus className={classes.formField}
                error={Boolean(formErrors.action_code)}
                helperText={formErrors.action_code} inputProps={{ maxLength: 50 }}
                label="Code" name="action_code" onChange={handleFormChange}
                required size="small" value={form.action_code} variant="outlined" />
              <TextField className={classes.formField}
                error={Boolean(formErrors.action_name)}
                helperText={formErrors.action_name} inputProps={{ maxLength: 250 }}
                label="Nom complet" name="action_name" onChange={handleFormChange}
                required size="small" value={form.action_name} variant="outlined" />
            </div>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button className={classes.cancelButton} disabled={saving}
              onClick={closeFormModal}>Annuler</Button>
            <Button className={classes.saveButton} disabled={saving}
              type="submit" variant="contained">
              {saving ? <CircularProgress color="inherit" size={20} /> :
                editingItem ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog aria-labelledby="delete-action-title"
        onClose={() => !deleting && setItemToDelete(null)} open={Boolean(itemToDelete)}>
        <DialogTitle id="delete-action-title">Supprimer le type d’action</DialogTitle>
        <DialogContent><DialogContentText>
          Confirmez-vous la suppression de « {itemToDelete?.action_code} —{" "}
          {itemToDelete?.action_name} » ?
        </DialogContentText></DialogContent>
        <DialogActions>
          <Button className={classes.cancelButton} disabled={deleting}
            onClick={() => setItemToDelete(null)}>Annuler</Button>
          <Button className={classes.confirmDeleteButton} disabled={deleting}
            onClick={handleDelete} variant="contained">
            {deleting ? <CircularProgress color="inherit" size={20} /> : "Supprimer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={4500} onClose={closeNotification} open={notification.open}>
        <Alert elevation={6} onClose={closeNotification}
          severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </SMQMainContainer>
  );
}

export default ActionTypes;
